import React from 'react'
import PropTypes from 'prop-types'
import i18n from 'i18next'
import Select from '../../Components/Select.jsx'
import Input from '../../Components/Input.jsx'
import Spinner from '../../Components/Spinner.jsx'
import DeliveryDateModal from '../../Components/DeliveryDateModal.jsx'
import {Calendar} from '../../Icons'
import {UploadInput} from '../../Uploader'
import * as storage from '../../Services/storage.jsx'

function InstantQuoteForm({
  // Form state
  sourceLanguage,
  targetLanguages,
  subject,
  deliveryDate,
  languagesState,
  mostPopularTargetLanguages,
  subjects,
  isFormSubmitted,
  isDeliveryDateModalVisible,
  deliveryMaxSelectableDate,
  selectedTimeZone,
  quotesArePending,
  isFormChanged,
  // Handlers
  handleSetSourceLanguage,
  handleSetTargetLanguages,
  toggleTargetLanguage,
  resetSelectedTargetOptions,
  handleSetSubject,
  getDeliveryDateText,
  showDeliveryDateModal,
  onDismissDeliveryDateModal,
  setDeliveryDateAndTimezone,
  handleSubmit,
}) {
  return (
    <form onSubmit={handleSubmit} aria-label="Request an instant quote">
      <div className={'instant-quote-form__row'}>
        <Select
          name={'source'}
          className={'instant-quote-form__input'}
          label={i18n.t('Pages.InstantQuote.sourceLanguageLabel')}
          placeholder={i18n.t('Pages.InstantQuote.sourceLanguageLabel')}
          options={languagesState}
          activeOption={sourceLanguage ? sourceLanguage : undefined}
          onSelect={handleSetSourceLanguage}
          onChange={storage.storeInstantQuoteSourceLanguage}
          isValid={sourceLanguage && !!sourceLanguage.id}
          showValidation={isFormSubmitted}
          showSearchBar={true}
          dataTestId="selected-source-language"
        />
        <Select
          name={'target'}
          className={'instant-quote-form__input'}
          label={`${
            targetLanguages.length > 1
              ? i18n.t('Pages.InstantQuote.targetLanguagesMultipleLabel', {
                  languages: targetLanguages.length,
                })
              : i18n.t('Pages.InstantQuote.targetLanguagesLabel')
          }`}
          placeholder={`${
            targetLanguages.length > 1
              ? i18n.t('Pages.InstantQuote.targetLanguagesMultipleLabel', {
                  languages: targetLanguages.length,
                })
              : i18n.t('Pages.InstantQuote.targetLanguagesLabel')
          }`}
          options={languagesState}
          activeOptions={targetLanguages}
          onSelect={handleSetTargetLanguages}
          onToggleOption={toggleTargetLanguage}
          isValid={targetLanguages && targetLanguages.length > 0}
          showValidation={isFormSubmitted}
          showSearchBar={true}
          isMultipleSelect={true}
          optionsSelectedCopySingular={(selectedOptionsLength) =>
            i18n.t('Pages.InstantQuote.selectedLanguageSingular', {
              number: selectedOptionsLength,
            })
          }
          optionsSelectedCopyPlural={(selectedOptionsLength) =>
            i18n.t('Pages.InstantQuote.selectedLanguagePlural', {
              number: selectedOptionsLength,
            })
          }
          resetSelectedOptions={resetSelectedTargetOptions}
          mostPopularOptions={mostPopularTargetLanguages}
          showMultipleSelectLabel={i18n.t(
            'Pages.InstantQuote.addMultipleLanguages',
          )}
          dataTestId="selected-target-languages"
        />
        <UploadInput
          label={i18n.t('Pages.InstantQuote.wordCountLabel')}
          placeholder={i18n.t('Pages.InstantQuote.wordCountLabel')}
          chooseFilesLabel={i18n.t(
            'Components.WordCountOrUpload.uploadFileButton',
          )}
          chooseFilesLabelWithFiles={i18n.t(
            'Components.WordCountOrUpload.editFileButton',
          )}
          onChange={Function.prototype}
          uploaderProps={{
            headerLabel: i18n.t('Components.UploaderModal.title'),
            submitLabel: i18n.t(
              'Components.UploaderModal.submitButtonForWordCount',
            ),
          }}
          wordCountEnabled={true}
        />
      </div>
      <div className={'instant-quote-form__row'}>
        <Select
          name={'subject'}
          className={'instant-quote-form__input'}
          label={i18n.t('Pages.InstantQuote.subjectLabel')}
          placeholder={i18n.t('Pages.InstantQuote.subjectLabel')}
          options={subjects}
          activeOption={subject ? subject : undefined}
          onSelect={handleSetSubject}
          isValid={subject && !!subject.id}
          showValidation={isFormSubmitted}
          showSearchBar={true}
          dataTestId="selected-subject"
        />
        <Input
          className={
            'instant-quote-form__input instant-quote-form__input--delivery-date'
          }
          label={i18n.t('Pages.InstantQuote.deliveryDateLabel')}
          placeholder={i18n.t('Pages.InstantQuote.deliveryDateLabel')}
          value={getDeliveryDateText()}
          onPress={showDeliveryDateModal}
          onFocus={showDeliveryDateModal}
          isValid={true}
          showValidation={isFormSubmitted}
          type={'button'}
          icon={Calendar}
        />
        {quotesArePending ? (
          <div
            className={
              'instant-quote-form__input instant-quote-form__input--pending button button--secondary'
            }
          >
            <Spinner isGrey={true} />
          </div>
        ) : (
          <button
            className={`instant-quote-form__input instant-quote-form__submit ${
              isFormSubmitted && !isFormChanged ? 'button--secondary' : ''
            }${isFormSubmitted && !isFormChanged ? ' button--tertiary' : ''}`}
            type={'submit'}
          >
            {isFormSubmitted
              ? i18n.t('Pages.InstantQuote.updatePricesButton')
              : i18n.t('Pages.InstantQuote.showPricesButton')}
          </button>
        )}
      </div>

      {/* DELIVERY DATE */}
      {isDeliveryDateModalVisible && (
        <DeliveryDateModal
          onDismiss={onDismissDeliveryDateModal}
          onUpdateDate={setDeliveryDateAndTimezone}
          deliveryDate={deliveryDate}
          deliveryMaxSelectableDate={deliveryMaxSelectableDate}
          currentTimeZone={selectedTimeZone}
        />
      )}
    </form>
  )
}

InstantQuoteForm.propTypes = {
  sourceLanguage: PropTypes.object,
  targetLanguages: PropTypes.array.isRequired,
  subject: PropTypes.object,
  deliveryDate: PropTypes.object.isRequired,
  languagesState: PropTypes.array.isRequired,
  mostPopularTargetLanguages: PropTypes.array.isRequired,
  subjects: PropTypes.array.isRequired,
  isFormSubmitted: PropTypes.bool.isRequired,
  isDeliveryDateModalVisible: PropTypes.bool.isRequired,
  deliveryMaxSelectableDate: PropTypes.object,
  selectedTimeZone: PropTypes.object.isRequired,
  quotesArePending: PropTypes.bool.isRequired,
  isFormChanged: PropTypes.bool.isRequired,
  handleSetSourceLanguage: PropTypes.func.isRequired,
  handleSetTargetLanguages: PropTypes.func.isRequired,
  toggleTargetLanguage: PropTypes.func.isRequired,
  resetSelectedTargetOptions: PropTypes.func.isRequired,
  handleSetSubject: PropTypes.func.isRequired,
  getDeliveryDateText: PropTypes.func.isRequired,
  showDeliveryDateModal: PropTypes.func.isRequired,
  onDismissDeliveryDateModal: PropTypes.func.isRequired,
  setDeliveryDateAndTimezone: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
}

export default InstantQuoteForm
